import { FormattedMessage, useIntl } from 'react-intl';

import type { ButtonVariants } from '@xing-com/button';
import { Button, TextButton } from '@xing-com/button';

import { Styled } from './light-box-footer.styles';

interface LightBoxFooterProps {
  buttons: LightBoxFooterButtonProps[];
  onClick: () => void;
  onClose?: () => void;
  buttonColor?: string;
  button1AriaLabel?: string;
  button2AriaLabel?: string;
  button1Color?: string;
  button2Color?: string;
}

interface LbfButtonProps {
  onClick?: () => void;
  buttonColor?: string;
  ariaLabel?: string;
  label?: string;
  variant?: string;
  i18nKey?: string;
}

interface LightBoxFooterButtonProps {
  id: string;
  text: string;
  clickAction: string;
  uri: string;
}

const LightBoxFooter = ({
  button1AriaLabel,
  button2AriaLabel,
  button1Color,
  button2Color = 'text',
  buttons,
  buttonColor,
  onClick,
  onClose,
}: LightBoxFooterProps): JSX.Element | null => {
  const allInvisible = buttons.every((button) => {
    const isClose = button.clickAction === 'CLOSE';
    const variant = isClose ? button2Color : button1Color || buttonColor;
    return variant === 'invisible';
  });

  if (allInvisible) {
    return null;
  }
  return (
    <Styled.Container>
      {buttons.map((button, index) => {
        const isClose = button.clickAction === 'CLOSE';
        const variant = isClose ? button2Color : button1Color || buttonColor;

        const props = {
          label: button.text,
          ariaLabel: isClose ? button2AriaLabel : button1AriaLabel,
          onClick: isClose ? onClose : onClick,
          i18nKey: isClose ? 'BUTTON_CLOSE' : 'BUTTON_FORWARD',
          variant: variant,
        };

        const Component = isClose
          ? LbfButton
          : button1Color
            ? LbfButton
            : LbfOldActionButtonWithLogic;

        return <Component {...props} key={index} />;
      })}
    </Styled.Container>
  );
};

const LbfButton = ({
  ariaLabel,
  variant,
  onClick,
  label,
  i18nKey,
}: LbfButtonProps): JSX.Element | null => {
  const intl = useIntl();
  const isText = variant === 'text';
  if (variant === 'invisible') {
    return null;
  }

  const allowedVariants = [
    'primary',
    'premium',
    'secondary',
    'tertiary',
    'proJobs',
    'proBusiness',
  ];

  const bVariant: ButtonVariants = allowedVariants.includes(variant as string)
    ? (variant as ButtonVariants)
    : ('primary' as ButtonVariants);

  const Component = isText ? TextButton : Button;

  return (
    <Component
      data-test-id="lightbox-footer-button"
      variant={bVariant}
      size="medium"
      onClick={onClick}
      aria-label={ariaLabel || label || intl.formatMessage({ id: i18nKey })}
    >
      {label || <FormattedMessage id={i18nKey} />}
    </Component>
  );
};

const LbfOldActionButtonWithLogic = ({
  ariaLabel,
  onClick,
  variant,
  label,
  i18nKey,
}: LbfButtonProps): JSX.Element => {
  const intl = useIntl();

  function mapVariant(
    v?: string
  ): 'premium' | 'proJobs' | 'proBusiness' | 'primary' {
    switch (v?.toLowerCase()) {
      case 'premium':
        return 'premium';
      case 'projobs':
        return 'proJobs';
      case 'probusiness':
        return 'proBusiness';
      default:
        return 'primary';
    }
  }

  const forwardButtonStyle = {
    variant: mapVariant(variant),
  };

  return variant?.toLowerCase() === 'black' ? (
    <Styled.BlackButton
      data-test-id="lightbox-footer-button"
      aria-label={ariaLabel || label || intl.formatMessage({ id: i18nKey })}
      size="medium"
      onClick={onClick}
    >
      {label || <FormattedMessage id={i18nKey} />}
    </Styled.BlackButton>
  ) : (
    <Styled.StyledButton
      {...forwardButtonStyle}
      data-test-id="lightbox-footer-button"
      aria-label={ariaLabel || label || intl.formatMessage({ id: i18nKey })}
      size="medium"
      onClick={onClick}
    >
      {label || <FormattedMessage id={i18nKey} />}
    </Styled.StyledButton>
  );
};

export { LightBoxFooter };
